import { useState } from 'react';
import { useForm, type FieldErrors } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Button } from '@/components/ui/button';
import { Form } from '@/components/ui/form';
import { Loader2 } from 'lucide-react';
import axiosInstance from '@/lib/axios';
import { toast } from 'sonner';
import { mutate } from 'swr';
import type { UserWithCommunities } from '@/types';
import { ImageCropper } from '@/components/ui/image-cropper';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Label } from '@/components/ui/label';
import { FormInputField } from '@/components/forms/form-input-field';
import { FormWheelDatePickerField } from '@/components/forms/form-wheel-date-picker-field';
import { FormSelectField } from '@/components/forms/form-select-field';
import { FormFileUploadField } from '@/components/forms/form-file-upload-field';
import { ImageFileInput } from '@/components/forms/image-file-input';

const profileCompletionSchema = z.object({
  firstName: z.string().optional(),
  middleName: z.string().optional(),
  lastName: z.string().optional(),
  dob: z.string().optional().nullable(),
  anniversaryDate: z.string().optional().nullable(),
  gender: z.string().optional().nullable(),
  bloodGroup: z.string().optional().nullable(),
  aadhaarCardUrl: z.string().optional().nullable(),
  address: z
    .object({
      line1: z.string().optional(),
      line2: z.string().optional(),
      city: z.string().optional(),
      state: z.string().optional(),
      country: z.string().optional(),
      pincode: z.string().optional(),
    })
    .optional()
    .nullable(),
});

type ProfileCompletionValues = z.infer<typeof profileCompletionSchema>;

interface ProfileCompletionStepProps {
  user: UserWithCommunities;
  missingFields: string[];
  onComplete: () => void;
  onCancel: () => void;
}

const GENDER_OPTIONS = [
  { value: 'MALE', label: 'Male' },
  { value: 'FEMALE', label: 'Female' },
  { value: 'OTHER', label: 'Other' },
  { value: 'PREFER_NOT_TO_SAY', label: 'Prefer not to say' },
];

const BLOOD_GROUP_OPTIONS = ['A+', 'A-', 'B+', 'B-', 'AB+', 'AB-', 'O+', 'O-'].map((v) => ({
  value: v,
  label: v,
}));

export function ProfileCompletionStep({
  user,
  missingFields,
  onComplete,
  onCancel,
}: ProfileCompletionStepProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [converting, setConverting] = useState(false);
  const [profileImage, setProfileImage] = useState<string | null>(user.image || null);
  const [selectedImageFile, setSelectedImageFile] = useState<string | null>(null);

  const form = useForm<ProfileCompletionValues>({
    resolver: zodResolver(profileCompletionSchema),
    defaultValues: {
      firstName: user.firstName || '',
      middleName: user.middleName || '',
      lastName: user.lastName || '',
      dob: user.dob || null,
      anniversaryDate: user.anniversaryDate || null,
      gender: user.gender || null,
      bloodGroup: user.bloodGroup || null,
      aadhaarCardUrl: user.aadhaarCardUrl || null,
      address:
        user.address ||
        (missingFields.includes('address')
          ? {
              line1: '',
              line2: '',
              city: '',
              state: '',
              country: '',
              pincode: '',
            }
          : null),
    },
  });

  const handlePreparedDataUrl = (dataUrl: string) => {
    setSelectedImageFile(dataUrl);
  };

  const onCropComplete = async (blob: Blob) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      setProfileImage(reader.result as string);
      setSelectedImageFile(null);
    };
    reader.readAsDataURL(blob);
  };

  async function onSubmit(values: ProfileCompletionValues) {
    setIsSubmitting(true);
    try {
      const payload: Partial<ProfileCompletionValues> & { image?: string } = {};
      if (missingFields.includes('image')) payload.image = profileImage || '';
      if (missingFields.includes('aadhaarCard')) payload.aadhaarCardUrl = values.aadhaarCardUrl;
      if (missingFields.includes('firstName')) payload.firstName = values.firstName;
      if (missingFields.includes('middleName')) payload.middleName = values.middleName;
      if (missingFields.includes('lastName')) payload.lastName = values.lastName;
      if (missingFields.includes('dob')) payload.dob = values.dob;
      if (missingFields.includes('anniversaryDate'))
        payload.anniversaryDate = values.anniversaryDate;
      if (missingFields.includes('gender')) payload.gender = values.gender;
      if (missingFields.includes('bloodGroup')) payload.bloodGroup = values.bloodGroup;
      if (missingFields.includes('address')) payload.address = values.address;

      await axiosInstance.patch('/api/user', payload);
      await mutate('/api/user');
      toast.success('Profile updated');
      onComplete();
    } catch (error) {
      console.error(error);
      toast.error('Failed to update profile');
    } finally {
      setIsSubmitting(false);
    }
  }

  const onInvalid = (errors: FieldErrors<ProfileCompletionValues>) => {
    console.error('Form validation errors:', errors);

    const getErrorMessage = (errorObj: Record<string, unknown>): string[] => {
      let messages: string[] = [];
      for (const key in errorObj) {
        const error = errorObj[key];
        if (
          error &&
          typeof error === 'object' &&
          'message' in error &&
          typeof error.message === 'string'
        ) {
          messages.push(error.message);
        } else if (error && typeof error === 'object') {
          messages = [...messages, ...getErrorMessage(error as Record<string, unknown>)];
        }
      }
      return messages;
    };

    const messages = getErrorMessage(errors);
    const errorString = messages.length > 0 ? messages.join(', ') : 'Invalid input';

    toast.error(`Please fix the following: ${errorString}`);
  };

  const { control } = form;

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit, onInvalid)} className="space-y-4">
        {missingFields.includes('image') && (
          <div className="space-y-3">
            <Label>
              Profile Photo <span className="text-destructive">*</span>
            </Label>
            <div className="flex flex-col items-center gap-4">
              {profileImage && (
                <Avatar className="h-24 w-24">
                  <AvatarImage src={profileImage} alt="Profile" />
                  <AvatarFallback>
                    {user.firstName?.[0]}
                    {user.lastName?.[0]}
                  </AvatarFallback>
                </Avatar>
              )}
              <ImageFileInput
                className="max-w-xs"
                accept="image/*"
                onPreparedDataUrl={handlePreparedDataUrl}
                onPreparingChange={setConverting}
                disabled={converting}
              />
              {converting && (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Converting photo…
                </p>
              )}
            </div>
          </div>
        )}

        {missingFields.includes('aadhaarCard') && (
          <FormFileUploadField
            control={control}
            name="aadhaarCardUrl"
            label="Aadhaar Card"
            description="Upload a clear image or PDF of your Aadhaar card. This is stored securely and not shown on your public profile."
            uploadFolder="aadhaar-cards"
            required
          />
        )}

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {missingFields.includes('firstName') && (
            <FormInputField
              control={control}
              name="firstName"
              label="First Name"
              placeholder="Enter your first name"
              required
            />
          )}
          {missingFields.includes('middleName') && (
            <FormInputField
              control={control}
              name="middleName"
              label="Middle Name"
              placeholder="Enter your middle name"
            />
          )}
          {missingFields.includes('lastName') && (
            <FormInputField
              control={control}
              name="lastName"
              label="Last Name"
              placeholder="Enter your last name"
              required
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {missingFields.includes('dob') && (
            <FormWheelDatePickerField
              control={control}
              name="dob"
              label="Date of Birth"
              placeholder="Select date"
              required
            />
          )}
          {missingFields.includes('anniversaryDate') && (
            <FormWheelDatePickerField
              control={control}
              name="anniversaryDate"
              label="Anniversary Date"
              placeholder="Select date"
            />
          )}
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {missingFields.includes('gender') && (
            <FormSelectField
              control={control}
              name="gender"
              label="Gender"
              placeholder="Select gender"
              options={GENDER_OPTIONS}
              required
            />
          )}
          {missingFields.includes('bloodGroup') && (
            <FormSelectField
              control={control}
              name="bloodGroup"
              label="Blood Group"
              placeholder="Select blood group"
              options={BLOOD_GROUP_OPTIONS}
              required
            />
          )}
        </div>

        {missingFields.includes('address') && (
          <div className="space-y-4 pt-4 border-t">
            <h4 className="font-medium text-sm text-muted-foreground">Address Details</h4>
            <FormInputField
              control={control}
              name="address.line1"
              label="Address Line 1"
              placeholder="House/Flat No., Street"
              required
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInputField
                control={control}
                name="address.city"
                label="City"
                placeholder="City"
                required
              />
              <FormInputField
                control={control}
                name="address.state"
                label="State"
                placeholder="State"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <FormInputField
                control={control}
                name="address.pincode"
                label="Pincode"
                placeholder="Pincode"
                required
              />
              <FormInputField
                control={control}
                name="address.country"
                label="Country"
                placeholder="Country"
                required
              />
            </div>
          </div>
        )}

        <div className="flex justify-end gap-3 pt-6 border-t">
          <Button type="button" variant="outline" onClick={onCancel} disabled={isSubmitting}>
            Cancel
          </Button>
          <Button type="submit" disabled={isSubmitting} className="min-w-[100px]">
            {isSubmitting ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Saving...
              </>
            ) : (
              'Continue'
            )}
          </Button>
        </div>
      </form>

      {selectedImageFile && (
        <ImageCropper
          imageSrc={selectedImageFile}
          onCropComplete={onCropComplete}
          onCancel={() => setSelectedImageFile(null)}
          aspect={1}
        />
      )}
    </Form>
  );
}
